import { Module } from 'vuex'
import { ProductsState, Product, Category, ProductFilters } from '@/types'

const MOCK_CATEGORIES: Category[] = [
  { id: '1', name: 'Cement & Concrete', slug: 'cement-concrete' },
  { id: '2', name: 'Steel & Iron', slug: 'steel-iron' },
  { id: '3', name: 'Roofing', slug: 'roofing' },
  { id: '4', name: 'Tiles & Flooring', slug: 'tiles-flooring' },
  { id: '5', name: 'Plumbing', slug: 'plumbing' },
  { id: '6', name: 'Electrical', slug: 'electrical' },
  { id: '7', name: 'Paint & Finishing', slug: 'paint-finishing' },
  { id: '8', name: 'Timber & Wood', slug: 'timber-wood' }
]

const MOCK_PRODUCTS: Product[] = [
  {
    id: '1',
    name: 'Portland Cement 50kg',
    slug: 'portland-cement-50kg',
    description: 'High-quality Portland cement, ideal for structural concrete and masonry work.',
    price: 18000,
    comparePrice: 20000,
    category: MOCK_CATEGORIES[0],
    images: [{ id: '1', url: 'https://placehold.co/400x300/1a3566/ffffff?text=Cement', alt: 'Portland Cement' }],
    stock: 500,
    unit: 'bag',
    sku: 'CEM-001',
    featured: true,
    createdAt: '2024-01-01T00:00:00Z'
  },
  {
    id: '2',
    name: 'Reinforcement Bar Y12 (12m)',
    slug: 'rebar-y12',
    description: 'Grade 60 deformed steel reinforcement bar, 12mm diameter, 12 metres long.',
    price: 45000,
    category: MOCK_CATEGORIES[1],
    images: [{ id: '2', url: 'https://placehold.co/400x300/1a3566/ffffff?text=Rebar', alt: 'Rebar' }],
    stock: 200,
    unit: 'piece',
    sku: 'STL-001',
    featured: true,
    createdAt: '2024-01-02T00:00:00Z'
  },
  {
    id: '3',
    name: 'Iron Sheet 26 Gauge (8ft)',
    slug: 'iron-sheet-26g-8ft',
    description: 'Galvanized corrugated iron roofing sheet, 26 gauge, 8 feet long.',
    price: 12500,
    category: MOCK_CATEGORIES[2],
    images: [{ id: '3', url: 'https://placehold.co/400x300/1a3566/ffffff?text=Iron+Sheet', alt: 'Iron Sheet' }],
    stock: 350,
    unit: 'piece',
    sku: 'ROF-001',
    featured: true,
    createdAt: '2024-01-03T00:00:00Z'
  },
  {
    id: '4',
    name: 'Ceramic Floor Tile 60x60cm',
    slug: 'ceramic-floor-tile-60x60',
    description: 'Premium glazed ceramic floor tile, non-slip surface, suitable for indoor use.',
    price: 3200,
    comparePrice: 3800,
    category: MOCK_CATEGORIES[3],
    images: [{ id: '4', url: 'https://placehold.co/400x300/1a3566/ffffff?text=Floor+Tile', alt: 'Floor Tile' }],
    stock: 1200,
    unit: 'piece',
    sku: 'TIL-001',
    featured: false,
    createdAt: '2024-01-04T00:00:00Z'
  },
  {
    id: '5',
    name: 'uPVC Water Pipe 3/4" (6m)',
    slug: 'upvc-pipe-6m',
    description: 'Schedule 40 uPVC pressure pipe for cold water distribution.',
    price: 8500,
    category: MOCK_CATEGORIES[4],
    images: [{ id: '5', url: 'https://placehold.co/400x300/1a3566/ffffff?text=PVC+Pipe', alt: 'PVC Pipe' }],
    stock: 180,
    unit: 'piece',
    sku: 'PLM-001',
    featured: false,
    createdAt: '2024-01-05T00:00:00Z'
  },
  {
    id: '6',
    name: 'Exterior Wall Paint 20L',
    slug: 'exterior-wall-paint-20l',
    description: 'Weatherproof acrylic exterior paint, white base, tintable to any colour.',
    price: 62000,
    comparePrice: 68000,
    category: MOCK_CATEGORIES[6],
    images: [{ id: '6', url: 'https://placehold.co/400x300/1a3566/ffffff?text=Paint', alt: 'Wall Paint' }],
    stock: 80,
    unit: 'bucket',
    sku: 'PNT-001',
    featured: true,
    createdAt: '2024-01-06T00:00:00Z'
  },
  {
    id: '7',
    name: 'Hardwood Timber 2x4 (12ft)',
    slug: 'hardwood-timber-2x4',
    description: 'Kiln-dried hardwood timber, 2x4 inches, 12 feet, suitable for framing and formwork.',
    price: 9800,
    category: MOCK_CATEGORIES[7],
    images: [{ id: '7', url: 'https://placehold.co/400x300/1a3566/ffffff?text=Timber', alt: 'Timber' }],
    stock: 300,
    unit: 'piece',
    sku: 'TIM-001',
    featured: false,
    createdAt: '2024-01-07T00:00:00Z'
  },
  {
    id: '8',
    name: 'MCB Circuit Breaker 32A',
    slug: 'mcb-circuit-breaker-32a',
    description: 'Single pole miniature circuit breaker, 32A, DIN rail mount, IEC compliant.',
    price: 4500,
    category: MOCK_CATEGORIES[5],
    images: [{ id: '8', url: 'https://placehold.co/400x300/1a3566/ffffff?text=MCB', alt: 'Circuit Breaker' }],
    stock: 150,
    unit: 'piece',
    sku: 'ELC-001',
    featured: false,
    createdAt: '2024-01-08T00:00:00Z'
  }
]

const products: Module<ProductsState, unknown> = {
  namespaced: true,

  state: (): ProductsState => ({
    items: [],
    featured: [],
    current: null,
    categories: [],
    loading: false,
    error: null,
    filters: {
      search: '',
      categoryId: '',
      minPrice: null,
      maxPrice: null,
      sortBy: 'newest'
    }
  }),

  getters: {
    filteredProducts (state): Product[] {
      let list = [...state.items]
      const { search, categoryId, minPrice, maxPrice, sortBy } = state.filters

      if (search) {
        const q = search.toLowerCase()
        list = list.filter(p =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
        )
      }
      if (categoryId) {
        list = list.filter(p => p.category.id === categoryId)
      }
      if (minPrice !== null) {
        list = list.filter(p => p.price >= (minPrice as number))
      }
      if (maxPrice !== null) {
        list = list.filter(p => p.price <= (maxPrice as number))
      }

      switch (sortBy) {
        case 'price_asc': list.sort((a, b) => a.price - b.price); break
        case 'price_desc': list.sort((a, b) => b.price - a.price); break
        case 'newest': list.sort((a, b) => b.createdAt.localeCompare(a.createdAt)); break
      }

      return list
    }
  },

  mutations: {
    SET_LOADING (state, loading: boolean) { state.loading = loading },
    SET_ERROR (state, error: string | null) { state.error = error },
    SET_PRODUCTS (state, products: Product[]) { state.items = products },
    SET_FEATURED (state, products: Product[]) { state.featured = products },
    SET_CATEGORIES (state, categories: Category[]) { state.categories = categories },
    SET_CURRENT (state, product: Product | null) { state.current = product },
    SET_FILTERS (state, filters: Partial<ProductFilters>) {
      state.filters = { ...state.filters, ...filters }
    },
    ADD_PRODUCT (state, product: Product) { state.items.unshift(product) },
    UPDATE_PRODUCT (state, updated: Product) {
      const idx = state.items.findIndex(p => p.id === updated.id)
      if (idx !== -1) state.items.splice(idx, 1, updated)
    },
    DELETE_PRODUCT (state, id: string) {
      state.items = state.items.filter(p => p.id !== id)
    }
  },

  actions: {
    async fetchProducts ({ commit }) {
      commit('SET_LOADING', true)
      try {
        await new Promise(resolve => setTimeout(resolve, 600))
        commit('SET_PRODUCTS', MOCK_PRODUCTS)
        commit('SET_FEATURED', MOCK_PRODUCTS.filter(p => p.featured))
        commit('SET_CATEGORIES', MOCK_CATEGORIES)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchProduct ({ commit }, slug: string) {
      commit('SET_LOADING', true)
      commit('SET_CURRENT', null)
      try {
        await new Promise(resolve => setTimeout(resolve, 400))
        const product = MOCK_PRODUCTS.find(p => p.slug === slug) || null
        commit('SET_CURRENT', product)
        if (!product) commit('SET_ERROR', 'Product not found')
      } finally {
        commit('SET_LOADING', false)
      }
    },

    setFilters ({ commit }, filters: Partial<ProductFilters>) {
      commit('SET_FILTERS', filters)
    },

    async createProduct ({ commit }, product: Omit<Product, 'id' | 'createdAt'>) {
      const newProduct: Product = {
        ...product,
        id: Date.now().toString(),
        createdAt: new Date().toISOString()
      }
      commit('ADD_PRODUCT', newProduct)
      return newProduct
    },

    async updateProduct ({ commit }, product: Product) {
      commit('UPDATE_PRODUCT', product)
    },

    async deleteProduct ({ commit }, id: string) {
      commit('DELETE_PRODUCT', id)
    }
  }
}

export default products
